import { Page } from 'src/app/utils/pageable';
import * as LessonsV3 from 'src/app/col/model/v3/lessons';
import { Dates } from 'src/app/utils/calendar-utils';

function parseDate(value: unknown): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  return Dates.parse(String(value));
}

function parseDateField<T extends object, K extends keyof T>(obj: T | null | undefined, key: K): void {
  if (!obj || obj[key] == null) {
    return;
  }
  (obj as T)[key] = parseDate(obj[key]) as T[K];
}

function normalizeLessonMetric(metric: LessonsV3.V3LessonMetric | null | undefined): void {
  if (!metric) {
    return;
  }
  parseDateField(metric, 'plannedStartDate');
  parseDateField(metric, 'started');
}

function normalizeLessonFlags(flags: LessonsV3.ApiLessonFlag[] | null | undefined): void {
  if (!flags) {
    return;
  }
  flags.forEach((flag) => {
    parseDateField(flag, 'date');
    parseDateField(flag, 'solveDate');
  });
}

export function normalizeLessonView<T extends LessonsV3.LessonView>(lesson: T): T {
  if (!lesson) {
    return lesson;
  }
  parseDateField(lesson, 'createDate');
  normalizeLessonMetric(lesson.lessonMetric);

  if ('cancellationDetails' in lesson && lesson.cancellationDetails) {
    parseDateField(lesson.cancellationDetails, 'cancellationDate');
  }
  if ('flags' in lesson) {
    normalizeLessonFlags(lesson.flags ?? []);
  }
  return lesson;
}

export function normalizeLessonsPage(page: Page<LessonsV3.LessonView>): Page<LessonsV3.LessonView> {
  if (!page) {
    return page;
  }
  return {
    ...page,
    content: (page.content ?? []).map((lesson) => normalizeLessonView(lesson)),
  };
}

export function normalizeLessonMetricDto(metric: LessonsV3.V3LessonMetric): LessonsV3.V3LessonMetric {
  normalizeLessonMetric(metric);
  return metric;
}

export function normalizeLessonFlagsDto(flags: LessonsV3.ApiLessonFlag[]): LessonsV3.ApiLessonFlag[] {
  normalizeLessonFlags(flags);
  return flags;
}

export function normalizeLessonFlagDto(flag: LessonsV3.ApiLessonFlag): LessonsV3.ApiLessonFlag {
  normalizeLessonFlags(flag ? [flag] : []);
  return flag;
}

export function normalizeLessonBundlesPage(page: Page<LessonsV3.LessonBundle>): Page<LessonsV3.LessonBundle> {
  if (!page) {
    return page;
  }
  return {
    ...page,
    content: (page.content ?? []).map((bundle) => normalizeLessonBundle(bundle)),
  };
}

export function normalizeLessonBundle(bundle: LessonsV3.LessonBundle): LessonsV3.LessonBundle {
  if (!bundle) {
    return bundle;
  }
  parseDateField(bundle, 'date');
  parseDateField(bundle, 'expiryDate');
  return bundle;
}

export function normalizeLessonCommitsPage(page: Page<LessonsV3.LessonCommit>): Page<LessonsV3.LessonCommit> {
  if (!page) {
    return page;
  }
  return {
    ...page,
    content: (page.content ?? []).map((commit) => normalizeLessonCommit(commit)),
  };
}

export function normalizeLessonCommit(commit: LessonsV3.LessonCommit): LessonsV3.LessonCommit {
  if (!commit) {
    return commit;
  }
  parseDateField(commit, 'createDate');
  parseDateField(commit, 'updateDate');
  return commit;
}

export function normalizeProvaContext(
  context: LessonsV3.ColProvaContextDetails | null
): LessonsV3.ColProvaContextDetails | null {
  if (!context) {
    return context;
  }
  parseDateField(context, 'startExpiryDate');
  parseDateField(context, 'starterBuyDate');
  return context;
}
